import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { PLAN_PAGE, PlanPageView } from '@elva/ui';
import type { PlanState } from '@elva/ui';

const STATES: PlanState[] = ['dob', 'plan', 'book', 'question', 'wrong', 'locked', 'expired', 'called'];

/**
 * The patient-facing plan page (amendment 27 §6, staging fix 29 §1) — the public
 * route `/p/:code`, rendered outside the app shell so there is no staff nav.
 *
 * **Nothing but the practice shows before the date of birth.** The gate is
 * enforced in client state: the plan renders only for the right DOB (mock:
 * 03/14/1974, Todd Baird). `?state=` jumps to any of the nine screens for review;
 * `?staff=1` shows the Staff preview banner opened from Settings / the cheat sheet.
 */
export function PlanPagePublicPage() {
  const { code } = useParams<{ code: string }>();
  const [params] = useSearchParams();
  const override = params.get('state') ?? undefined;
  const staffPreview = params.get('staff') === '1';
  const initial: PlanState = override !== undefined && (STATES as string[]).includes(override) ? (override as PlanState) : 'dob';
  const [state, setState] = useState<PlanState>(initial);

  return (
    <main className="min-h-screen">
      {staffPreview ? (
        <div className="flex items-center justify-center gap-2 bg-[#242936] px-4 py-2 text-[12.5px] font-semibold text-white">
          <span className="rounded-[5px] bg-white/15 px-1.5 py-[1px] text-[10px] font-bold uppercase tracking-[0.06em]">Staff preview</span>
          You’re seeing the patient’s plan page. The patient enters their date of birth to open it.
        </div>
      ) : null}
      <PlanPageView
        state={state}
        data={{ ...PLAN_PAGE, code: code ?? PLAN_PAGE.code }}
        onOpen={(dob) => { setState(dob === PLAN_PAGE.dob ? 'plan' : 'wrong'); }}
        onBook={() => { setState((s) => (s === 'book' ? 'plan' : 'book')); }}
        onCallMe={() => { setState('called'); }}
        onAskQuestion={() => { setState('question'); }}
      />
    </main>
  );
}
